class CustomHeroBanner extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.sliderSelector = this.querySelector('.hero-banner__slider');
      this.slides = this.querySelectorAll('.hero-banner__slide');
  
      this.initialize = this.initialize.bind(this);
      this.initializeSlider = this.initializeSlider.bind(this);
    }
  
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initialize, { once: true });
      } else {
        this.initialize();
      }
    }
  
    initialize() {
      this.initializeLazySizes();
      this.initializeSlides();
      this.initializeSlider();
    }
  
    initializeSlides() {
      // Lazyload init
      if (window.lazySizes) {
        lazySizes.init();
      }
  
      // Images
      this.querySelectorAll('.hero-banner__media').forEach(img => {
        if (img.complete) {
          img.classList.add('lazyloaded');
        } else {
          img.addEventListener('load', () => img.classList.add('lazyloaded'), { once: true });
        }
      });
  
      // Videos
      this.querySelectorAll('.hero-banner__video').forEach(video => {
        video.addEventListener('loadedmetadata', () => video.classList.add('lazyloaded'), { once: true });
      });
    }
  
    initializeSlider() {
      const isSwiperEnabled = this.dataset.swiperEnabled === 'true';
  
      if (!isSwiperEnabled || this.slides.length <= 1 || !this.sliderSelector) {
        this.slides.forEach(slide => {
          slide.style.display = 'block';
        });
        return;
      }
  
      if (typeof Swiper === 'undefined') {
        this.loadSwiper().then(() => this.createSlider());
      } else {
        this.createSlider();
      }
    }
  
    loadSwiper() {
      return new Promise((resolve) => {
        if (document.querySelector('script[src*="swiper"]')) {
          const check = setInterval(() => {
            if (typeof Swiper !== 'undefined') {
              clearInterval(check);
              resolve();
            }
          }, 100);
          return;
        }
  
        // Swiper CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css';
        document.head.appendChild(link);
  
        // Swiper JS
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.js';
        script.onload = () => setTimeout(resolve, 100);
        document.head.appendChild(script);
      });
    }
  
    createSlider() {
      if (this.slider) return;
  
      const swiperWrapper = this.sliderSelector.querySelector('.swiper-wrapper');
      if (!swiperWrapper) {
        console.error('Swiper wrapper not found');
        return;
      }
  
      if (swiperWrapper.children.length === 0) {
        Array.from(this.slides).forEach(slide => swiperWrapper.appendChild(slide));
      }
  
      try {
        this.slider = new Swiper(this.sliderSelector, {
          loop: this.dataset.loop === 'true',
          effect: this.dataset.effect || 'slide',
          speed: parseInt(this.dataset.speed) || 500,
          autoplay: this.dataset.autoplay === 'true' ? {
            delay: parseInt(this.dataset.delay) || 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: this.dataset.pauseOnHover === 'true'
          } : false,
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            type: this.dataset.paginationType || 'bullets',
            clickable: true
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          on: {
            init: () => this.dispatchEvent(new CustomEvent('swiper:init', { detail: this.slider })),
            slideChange: () => this.dispatchEvent(new CustomEvent('swiper:slideChange', { detail: this.slider }))
          }
        });
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }
  
    initializeLazySizes() {
      if (typeof lazySizes === 'undefined' && !document.querySelector('script[src*="lazysizes"]')) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/lazysizes.min.js';
        script.integrity = 'sha512-q583ppKrCRc7N5O0n2nUiJ+suUv7Et1JGels4bXOaMFQcamPk9HjdUknZuuFjBNs7tsMuadge5k9RzdmO+1GQ==';
        script.crossOrigin = 'anonymous';
        script.referrerPolicy = 'no-referrer';
        document.body.appendChild(script);
      }
    }
  
    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }
  }
  
  // Register element only once
  if (!customElements.get('custom-hero-banner')) {
    customElements.define('custom-hero-banner', CustomHeroBanner);
  }
  
  // Shopify Theme Editor reload support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const banner = event.target.querySelector('custom-hero-banner');
      if (banner && typeof banner.initialize === 'function') {
        banner.initialize();
      }
    });
  }
  